import React from 'react';
import { Calculator, FileText, Loader2 } from 'lucide-react';

interface PopupData {
  gid: number;
  name?: string;
  area_sqm?: number;
  zone_code?: string;
  land_use_type?: string;
  village?: string;
  district?: string;
  tps_id?: number;
}

interface PopupComponentProps {
  data: PopupData;
  onBasicFSI: (gid: number) => void;
  onDetailedFSI: (gid: number) => void;
  isBasicLoading?: boolean;
  isDetailedLoading?: boolean;
}

const PopupComponent: React.FC<PopupComponentProps> = ({
  data,
  onBasicFSI,
  onDetailedFSI,
  isBasicLoading = false,
  isDetailedLoading = false
}) => {
  return (
    <div className="bg-white rounded-lg shadow-lg max-w-sm">
      {/* Header */}
      <div className="border-b border-gray-200 px-4 py-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {data.name || `Plot ${data.gid}`}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          GID: {data.gid} | TPS: {data.tps_id || 'N/A'}
        </p>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-4">
        {/* Plot Details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">Area</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">
              {data.area_sqm ? `${Math.round(data.area_sqm)} sq.m` : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">Zone</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">
              {data.zone_code || 'N/A'}
            </p>
          </div>
        </div>

        {/* Location Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">Village</p>
            <p className="text-sm text-gray-900 mt-1">
              {data.village || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">District</p>
            <p className="text-sm text-gray-900 mt-1">
              {data.district || 'N/A'}
            </p>
          </div>
        </div>

        {/* Land Use */}
        {data.land_use_type && (
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">Land Use</p>
            <p className="text-sm text-gray-900 mt-1">
              {data.land_use_type}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="border-t border-gray-200 px-4 py-3">
        <div className="flex space-x-2">
          <button
            onClick={() => onBasicFSI(data.gid)}
            disabled={isBasicLoading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {isBasicLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4" />
            )}
            Basic FSI
          </button>
          
          <button
            onClick={() => onDetailedFSI(data.gid)}
            disabled={isDetailedLoading}
            className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {isDetailedLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Detailed FSI
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupComponent;

// Helper function to generate HTML string for MapLibre popup
export const generatePopupHTML = (params: {
  gid: string | null;
  properties: any;
  parsedProperties: any;
  loadingBasicFSI: boolean;
  loadingDetailedFSI: boolean;
  isError?: boolean;
  hasNoGID?: boolean;
}): string => {
  const { gid, properties, parsedProperties, loadingBasicFSI, loadingDetailedFSI, isError, hasNoGID } = params;

  // Prefer flattened tile properties first, fall back to legacy nested JSON
  const pickValue = (...values: any[]) => {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return null;
  };

  const plotCode = pickValue(properties?.fps_code, parsedProperties?.fps_code);
  const village = pickValue(properties?.village, parsedProperties?.village);
  const plotNo = pickValue(properties?.fp_no, properties?.plot_no, parsedProperties?.fp_no, parsedProperties?.plot_no);
  const rawArea = pickValue(
    properties?.fp_area_final,
    properties?.plot_area,
    properties?.area,
    parsedProperties?.fp_area_final,
    parsedProperties?.plot_area,
    parsedProperties?.area
  );
  const areaText = rawArea && isFinite(Number(rawArea)) ? `${Math.round(Number(rawArea))} sq.m` : 'N/A';
  const tpsName = pickValue(properties?.tps_name, parsedProperties?.tps_name);
  const city = pickValue(properties?.city, properties?.district, parsedProperties?.city, parsedProperties?.district);
  const authority = pickValue(properties?.authority, properties?.authority_name, parsedProperties?.authority);
  const reservationType = pickValue(properties?.reservation_type, parsedProperties?.reservation_type);
  const reservationUse = pickValue(properties?.reservation_use, parsedProperties?.reservation_use);

  // Helper functions for land use
  const getLandUseCategory = (reserType: string | null, reserUse: string | null): string => {
    if (!reserType && !reserUse) return 'Residential';
    
    const type = (reserType || '').toLowerCase();
    const use = (reserUse || '').toLowerCase();
    const combined = `${type} ${use}`.toLowerCase();

    if (combined.includes('education') || combined.includes('school') || combined.includes('library') || 
        combined.includes('college') || combined.includes('technical institute') || combined.includes('bal bhavan')) {
      return 'Educational';
    }
    
    if (combined.includes('health') || combined.includes('medical') || combined.includes('hospital') || 
        combined.includes('dispensary') || combined.includes('maternity') || combined.includes('clinic')) {
      return 'Healthcare';
    }
    
    if (combined.includes('commercial') || combined.includes('market') || combined.includes('shopping') || 
        combined.includes('shop') || combined.includes('sale') || combined.includes('business')) {
      return 'Commercial';
    }
    
    if (combined.includes('industrial') || combined.includes('factory') || combined.includes('manufacturing')) {
      return 'Industrial';
    }
    
    if (combined.includes('recreation') || combined.includes('park') || combined.includes('garden') || 
        combined.includes('play') || combined.includes('open space') || combined.includes('green') ||
        combined.includes('swimming') || combined.includes('gymnasium') || combined.includes('sports')) {
      return 'Recreation';
    }
    
    if (combined.includes('police') || combined.includes('fire') || combined.includes('municipal') || 
        combined.includes('public purpose') || combined.includes('civic') || combined.includes('town hall') ||
        combined.includes('community') || combined.includes('social') || combined.includes('sanskar')) {
      return 'Public Services';
    }
    
    if (combined.includes('electric') || combined.includes('water') || combined.includes('drainage') || 
        combined.includes('pumping') || combined.includes('tube') || combined.includes('utility') ||
        combined.includes('sub') || combined.includes('substation') || combined.includes('telegraph')) {
      return 'Infrastructure';
    }
    
    if (combined.includes('bus') || combined.includes('parking') || combined.includes('transport') || 
        combined.includes('terminal') || combined.includes('stand') || combined.includes('taxi') ||
        combined.includes('octroi') || combined.includes('truck')) {
      return 'Transportation';
    }
    
    if (combined.includes('housing') || combined.includes('quarters') || combined.includes('residential') ||
        combined.includes('slum') || combined.includes('affordable')) {
      return 'Housing';
    }
    
    return 'Mixed Use';
  };

  const getLandUseColor = (category: string): string => {
    const colors: { [key: string]: string } = {
      'Residential': '#FFE4B5',
      'Housing': '#DEB887',
      'Commercial': '#FF6B6B',
      'Industrial': '#9B59B6',
      'Educational': '#3498DB',
      'Healthcare': '#2ECC71',
      'Recreation': '#27AE60',
      'Public Services': '#E67E22',
      'Infrastructure': '#95A5A6',
      'Transportation': '#F39C12',
      'Mixed Use': '#BDC3C7'
    };
    return colors[category] || '#BDC3C7';
  };

  if (hasNoGID) {
    return `
      <div class="bg-white rounded-lg shadow-lg border-0 overflow-hidden" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <div class="bg-emerald-500 text-white px-4 py-2">
          <h3 class="font-semibold">FPS Plot</h3>
        </div>
        <div class="p-4 space-y-2 text-sm">
          <div><span class="text-gray-500">Code:</span> <span class="font-medium">${plotCode || 'N/A'}</span></div>
          <div><span class="text-gray-500">Village:</span> <span class="font-medium">${village || 'N/A'}</span></div>
          <div class="text-xs text-orange-600 mt-2">No GID available for this plot</div>
        </div>
      </div>
    `;
  }

  if (isError) {
    return `
      <div class="bg-white rounded-lg shadow-lg border-0 overflow-hidden" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <div class="bg-emerald-500 text-white px-4 py-2">
          <h3 class="font-semibold">Plot ${gid}</h3>
        </div>
        <div class="p-4 space-y-2 text-sm">
            <div><span class="text-gray-500">Code:</span> <span class="font-medium">${plotCode || 'N/A'}</span></div>
            <div><span class="text-gray-500">Village:</span> <span class="font-medium">${village || 'N/A'}</span></div>
            <div><span class="text-gray-500">Plot No:</span> <span class="font-medium">${plotNo || 'N/A'}</span></div>
            <div><span class="text-gray-500">Area:</span> <span class="font-medium">${areaText}</span></div>
            <div><span class="text-gray-500">TPS:</span> <span class="font-medium">${tpsName || 'N/A'}</span></div>
        </div>
        <div class="bg-gray-50 px-4 py-2 space-y-2">
          <button 
            onclick="window.selectPlot('${gid}')"
            class="w-full bg-[#179246] hover:bg-[#147a3c] text-white text-sm py-2 rounded transition-colors duration-200"
          >
            Select Plot
          </button>
          <div class="flex gap-2">
            <button 
              onclick="window.basicFSI('${gid}')"
              class="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 rounded transition-colors duration-200 ${loadingBasicFSI ? 'opacity-50 cursor-not-allowed' : ''}"
              ${loadingBasicFSI ? 'disabled' : ''}
            >
              ${loadingBasicFSI ? '<div class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>' : ''}
              Basic FSI
            </button>
            <button 
              onclick="window.detailedFSI('${gid}')"
              class="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm py-2 rounded transition-colors duration-200 ${loadingDetailedFSI ? 'opacity-50 cursor-not-allowed' : ''}"
              ${loadingDetailedFSI ? 'disabled' : ''}
            >
              ${loadingDetailedFSI ? '<div class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>' : ''}
              Detailed FSI
            </button>
          </div>
        </div>
      </div>
    `;
  }

  const landUseCategory = getLandUseCategory(reservationType, reservationUse);
  const landUseColor = getLandUseColor(landUseCategory);

  return `
    <div class="bg-white rounded-lg shadow-xl border-0 overflow-hidden" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      <!-- Header -->
      <div class="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-3">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold">Plot ${gid}</h3>
          <div class="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
            FPS Plot
          </div>
        </div>
      </div>
      
      <!-- Content -->
      <div class="p-4 space-y-3">
        <!-- Primary Info -->
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div class="flex flex-col">
            <span class="text-gray-500 text-xs font-medium uppercase tracking-wide">Plot Code</span>
            <span class="text-gray-900 font-semibold">${plotCode || 'N/A'}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-gray-500 text-xs font-medium uppercase tracking-wide">Village</span>
            <span class="text-gray-900 font-semibold">${village || 'N/A'}</span>
          </div>
        </div>
        
        <!-- Plot Details -->
        <div class="border-t border-gray-100 pt-3">
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div class="flex flex-col">
              <span class="text-gray-500 text-xs font-medium">Plot No</span>
              <span class="text-gray-700">${plotNo || 'N/A'}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-gray-500 text-xs font-medium">Area</span>
              <span class="text-gray-700">${areaText}</span>
            </div>
          </div>
        </div>
        
        <!-- Location Info -->
        <div class="border-t border-gray-100 pt-3">
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div class="flex flex-col">
              <span class="text-gray-500 text-xs font-medium">City</span>
              <span class="text-gray-700">${city || 'N/A'}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-gray-500 text-xs font-medium">Authority</span>
              <span class="text-gray-700">${authority || 'N/A'}</span>
            </div>
          </div>
        </div>
        
        <!-- TPS Info -->
        ${tpsName ? `
          <div class="border-t border-gray-100 pt-3">
            <div class="flex justify-between items-center text-sm">
              <div class="flex flex-col">
                <span class="text-gray-500 text-xs font-medium">TPS Name</span>
                <span class="text-gray-700">${tpsName}</span>
              </div>
            </div>
          </div>
        ` : ''}
        
        <!-- Land Use Info -->
        ${(reservationType || reservationUse) ? `
          <div class="border-t border-gray-100 pt-3">
            <div class="text-sm">
              <div class="flex flex-col mb-2">
                <span class="text-gray-500 text-xs font-medium">Land Use Category</span>
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded-full" style="background-color: ${landUseColor}"></div>
                  <span class="text-gray-700 font-medium">${landUseCategory}</span>
                </div>
              </div>
              ${reservationType ? `
                <div class="text-xs text-gray-500">
                  <span class="font-medium">Type:</span> ${reservationType}
                </div>
              ` : ''}
              ${reservationUse ? `
                <div class="text-xs text-gray-500">
                  <span class="font-medium">Use:</span> ${reservationUse}
                </div>
              ` : ''}
            </div>
          </div>
        ` : ''}
      </div>
      
      <!-- Actions -->
      <div class="bg-gray-50 px-4 py-3 space-y-3">
        <button 
          onclick="window.selectPlot('${gid}')"
          class="w-full bg-[#179246] hover:bg-[#147a3c] text-white text-sm font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          Select Plot
        </button>
        
        <div class="flex gap-2">
          <button 
            onclick="window.basicFSI('${gid}')"
            class="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-1 ${loadingBasicFSI ? 'opacity-50 cursor-not-allowed' : ''}"
            ${loadingBasicFSI ? 'disabled' : ''}
          >
            ${loadingBasicFSI ? `
              <div class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ` : `
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
            `}
            Basic FSI
          </button>
          <button 
            onclick="window.detailedFSI('${gid}')"
            class="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-1 ${loadingDetailedFSI ? 'opacity-50 cursor-not-allowed' : ''}"
            ${loadingDetailedFSI ? 'disabled' : ''}
          >
            ${loadingDetailedFSI ? `
              <div class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ` : `
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            `}
            Detailed FSI
          </button>
        </div>
        
        <button 
          onclick="window.customFSIReport('${gid}')"
          class="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-sm font-medium py-2.5 px-4 rounded-md transition-all duration-200 flex items-center justify-center gap-2 shadow-md"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
          </svg>
          Custom FSI Report
        </button>
      </div>
    </div>
  `;
};
